import {FC} from 'react';
import {useTranslation} from 'react-i18next';
import {Form} from 'formik';
import * as Yup from 'yup';
import {List} from '@tickchak/react-share/app';
import {TShow} from '../../../../../hooks/useShow';
import BasicInput from '../../../../../component/input/basicInput/BasicInput';
import FormContext from '../../../../components/formContext/FormContext';
import BackArrow from '../../../../../assets/icons/BackArrow';
import {createSlot} from '../../../../../redux/timetable/timetableActions';
import {getNow, getStartOfDay} from '../../../../../library/dates';
import * as css from './popupCard.css';

interface AddSlotPopupProps {
    show: TShow;
    dayTimestamp: number;
    onAdd: (tsid: number) => void;
}

const parseHHmm = (value: string): [number, number] | null => {
    const match = value.match(/^(\d{1,2}):(\d{2})/);
    if (!match) return null;
    const h = Number(match[1]);
    const m = Number(match[2]);
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return [h, m];
};

const AddSlotPopup: FC<AddSlotPopupProps> = ({show, dayTimestamp, onAdd}) => {
    const {t} = useTranslation('timetable');

    if (!show.isOpen) return null;

    const handleClose = () => show.close();

    const validationSchema = Yup.object({
        time: Yup.string()
            .required(t('fieldRequired'))
            .test('hhmm', t('fieldRequired'), v => !!v && parseHHmm(v) !== null),
    });

    const handleSubmit = async (_changed: unknown, {time}: {time: string}) => {
        const parsed = parseHHmm(time);
        if (!parsed) return;
        const [h, m] = parsed;
        const baseDay = dayTimestamp || getStartOfDay(getNow());
        const slot = await createSlot(baseDay + h * 3600 + m * 60);
        if (slot) onAdd(slot.tsid);
        handleClose();
    };

    return (
        <FormContext initialValues={{time: ''}} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {({submitForm}: {submitForm: () => void}) => (
                <List
                    show={show}
                    data-cy="addSlotPop"
                    position={{width: '375px', height: '432px'}}
                    ariaLabel={t('addSlotTitle')}
                >
                    <List.Header
                        fixed
                        title={t('addSlotTitle')}
                        onBack={handleClose}
                        backIcon={<BackArrow />}
                    />
                    <List.Items>
                        <css.card>
                            <Form placeholder="" onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}}>
                                <BasicInput
                                    name="time"
                                    text={t('addSlotInputLabel')}
                                    icon="hourglassOutline"
                                    dataCy="addSlotInput"
                                    types={{fieldType: 'input', inputType: 'time'}}
                                    events={{}}
                                    className=""
                                    forwardRef={null}
                                />
                            </Form>
                        </css.card>
                    </List.Items>
                    <List.Footer fixed>
                        <List.Footer.Close text={t('cancel')} size="category" onClick={handleClose} />
                        <List.Footer.Button
                            text={t('add')}
                            variant="brand"
                            size="category"
                            onClick={submitForm}
                        />
                    </List.Footer>
                </List>
            )}
        </FormContext>
    );
};

export default AddSlotPopup;
